import { Instance, SnapshotIn, types, flow, getEnv, cast } from 'mobx-state-tree';

import { IStoresEnv } from '@core/storesEnv';
import { downloadBlobAsFile } from '@shared/helpers';
import { performGetProcedures } from 'Procedures/services';
import { ProcedureStore } from 'Procedures/stores/Procedure.store';
import { ALL_PROCEDURES_ID } from 'Procedures/domain/constants';

import { IntegrationProcedure } from 'Integrations/domain/IntegrationProcedure';
import { IntegrationDeployment } from 'Integrations/domain/IntegrationDeployment';
import { IntegrationExportSettings } from 'Integrations/domain/IntegrationExportSettings';
import { performExportProcedures, performGetDeployments } from 'Integrations/services';

// The modal shows the whole company catalogue in one scrollable list, so it asks
// for a single oversized page instead of paginating.
const PROCEDURES_PAGE_SIZE = 500;
const SORT_ASCENDING = 0;

const DeploymentStore = types.model('IntegrationDeploymentStore', {
    id: types.string,
    name: types.string,
    productName: types.maybeNull(types.string),
    description: types.maybeNull(types.string),
});

export const IntegrationsStoreInferred = types
    .model('IntegrationsStoreInferred', {
        // Typed copies, used to render the modal.
        proceduresList: types.optional(types.array(ProcedureStore), []),
        deploymentsList: types.optional(types.array(DeploymentStore), []),
    })
    .volatile(() => ({
        // Raw search response entities, forwarded verbatim in the export body.
        // Read only inside actions, never rendered.
        rawProcedures: [] as IntegrationProcedure[],
    }))
    .actions(self => {
        const { api } = getEnv<IStoresEnv>(self);

        const loadProcedures = flow(function* () {
            const result = yield performGetProcedures(
                api,
                {
                    payload: {
                        page: 0,
                        limit: PROCEDURES_PAGE_SIZE,
                        searchText: '',
                        sortFieldName: 'name',
                        sortDirection: SORT_ASCENDING,
                        inGroup: ALL_PROCEDURES_ID,
                        inGroupId: '',
                    },
                    errorHandlers: { DEFAULT: '' },
                },
            );

            if (result.success) {
                self.rawProcedures = result.data.entities ?? [];
                self.proceduresList = result.data.entities;
            }

            return result.success;
        });

        const loadDeployments = flow(function* () {
            const result = yield performGetDeployments(
                api,
                {
                    payload: null,
                    errorHandlers: { DEFAULT: '' },
                },
            );

            if (result.success) {
                // Only active targets can receive an export; secrets carried by
                // the response (apiKey, basicAuthKey, …) are deliberately dropped.
                const deployments: IntegrationDeployment[] = result.data ?? [];
                self.deploymentsList = cast(
                    deployments
                        .filter(deployment => deployment.isActive)
                        .map(({ id, name, productName, description }) => ({
                            id,
                            name,
                            productName,
                            description,
                        })),
                );
            }

            return result.success;
        });

        // Returns the whole ApiResponse: the caller reports the failure itself
        // (hence the empty DEFAULT handler) and wants the backend's message.
        const exportProcedures = flow(function* (
            deploymentId: string,
            settings: IntegrationExportSettings,
            procedures: IntegrationProcedure[],
            filename: string,
        ) {
            const result = yield performExportProcedures(
                api,
                {
                    payload: { deploymentId, settings, procedures },
                    errorHandlers: { DEFAULT: '' },
                },
            );

            // The endpoint replies with the CSV itself: hand it to the browser.
            if (result.success && result.data instanceof Blob) {
                downloadBlobAsFile(result.data, filename);
            }

            return result;
        });

        const cleanData = () => {
            self.proceduresList = cast([]);
            self.deploymentsList = cast([]);
            self.rawProcedures = [];
        };

        return {
            loadProcedures,
            loadDeployments,
            exportProcedures,
            cleanData,
        };
    });

type IntegrationsStoreFactoryType = typeof IntegrationsStoreInferred;
interface IIntegrationsStoreFactory extends IntegrationsStoreFactoryType { }
export const IntegrationsStore: IIntegrationsStoreFactory = IntegrationsStoreInferred;
export interface IIntegrationsStore extends Instance<IIntegrationsStoreFactory> { }
export interface IIntegrationsStoreSnapshotIn extends SnapshotIn<IIntegrationsStore> { }
